import React, { useState } from 'react';
import { Copy, ExternalLink, Mail, MessageSquare, QrCode } from 'lucide-react';
import { logger } from '../utils/logger'

interface InvoicePaymentLinkProps {
  invoiceId: string;
  customerName?: string;
  customerEmail?: string;
  amount: number;
  description: string;
}

const InvoicePaymentLink: React.FC<InvoicePaymentLinkProps> = ({
  invoiceId,
  customerName,
  customerEmail,
  amount,
  description
}) => {
  const [copied, setCopied] = useState<string | null>(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailData, setEmailData] = useState({
    to: customerEmail || '',
    subject: `Pagamento da Fatura ${invoiceId}`,
    message: `Olá ${customerName || 'Cliente'},

Você recebeu uma nova fatura para pagamento:

📋 Fatura: ${invoiceId}
💰 Valor: R$ ${amount.toFixed(2)}
📝 Descrição: ${description}

🔗 Link de Pagamento: ${window.location.origin}/public/invoices/${invoiceId}/payment

Este link permite que você efetue o pagamento de forma segura através do MercadoPago.

Atenciosamente,
Equipe Financeira`
  });

  const paymentUrl = `${window.location.origin}/public/invoices/${invoiceId}/payment`;

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      logger.error('Erro ao copiar:', error);
    }
  };

  const sendEmail = async () => {
    try {
      const mailtoLink = `mailto:${emailData.to}?subject=${encodeURIComponent(emailData.subject)}&body=${encodeURIComponent(emailData.message)}`;
      window.open(mailtoLink);
    } catch (error) {
      logger.error('Erro ao abrir email:', error);
    }
  };

  const sendWhatsApp = () => {
    const message = `Olá ${customerName || 'Cliente'}! 

Você recebeu uma nova fatura para pagamento:

📋 Fatura: ${invoiceId}
💰 Valor: R$ ${amount.toFixed(2)}
📝 Descrição: ${description}

🔗 Link de Pagamento: ${paymentUrl}

Este link permite que você efetue o pagamento de forma segura.`;
    
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">🔗 Link de Pagamento</h3>
        <ExternalLink className="w-5 h-5 text-gray-400" />
      </div>

      <div className="space-y-4">
        {/* URL do Pagamento */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            URL de Pagamento
          </label>
          <div className="flex">
            <input
              type="text"
              value={paymentUrl}
              readOnly
              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg bg-gray-50 text-sm"
            />
            <button
              onClick={() => copyToClipboard(paymentUrl, 'url')}
              className="px-3 py-2 bg-blue-500 text-white rounded-r-lg hover:bg-blue-600 transition-colors"
            >
              {copied === 'url' ? '✓' : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* QR Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            QR Code
          </label>
          <div className="flex items-center space-x-2">
            <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
              <QrCode className="w-16 h-16 text-gray-400" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-600 mb-2">
                Escaneie o QR Code para acessar o link de pagamento
              </p>
              <button
                onClick={() => copyToClipboard(paymentUrl, 'qr')}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {copied === 'qr' ? '✓ Copiado!' : 'Copiar link'}
              </button>
            </div>
          </div>
        </div>

        {/* Botões de Envio */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowEmailForm(!showEmailForm)}
            className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <Mail className="w-4 h-4" />
            <span>Enviar por Email</span>
          </button>

          <button
            onClick={sendWhatsApp}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            <span>Enviar por WhatsApp</span>
          </button>
        </div>

        {/* Formulário de Email */}
        {showEmailForm && (
          <div className="border-t pt-4">
            <h4 className="text-md font-medium text-gray-900 mb-3">📧 Enviar por Email</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Para
                </label>
                <input
                  type="email"
                  value={emailData.to}
                  onChange={(e) => setEmailData(prev => ({ ...prev, to: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="cliente@exemplo.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assunto
                </label>
                <input
                  type="text"
                  value={emailData.subject}
                  onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mensagem
                </label>
                <textarea
                  value={emailData.message}
                  onChange={(e) => setEmailData(prev => ({ ...prev, message: e.target.value }))}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={sendEmail}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Enviar Email
                </button>
                <button
                  onClick={() => setShowEmailForm(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Informações de Segurança */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">🔒 Informações de Segurança</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• O link é seguro e utiliza criptografia SSL</li>
            <li>• Pagamentos processados pelo MercadoPago</li>
            <li>• Não é necessário criar conta no sistema</li>
            <li>• Link válido até o vencimento da fatura</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default InvoicePaymentLink;





